import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, KeyRound, Smartphone } from 'lucide-react';
import { supabase } from '@/services/supabase';

/**
 * Public landing page used as the redirect target for all Supabase Auth
 * email links (signup confirmation, magic link, recovery, etc.).
 *
 * The web dashboard is for operators only (clients use the mobile app), so
 * we deliberately do NOT show a link to the web panel here. We also
 * proactively sign out any session created by the email link, so a client
 * who clicks a recovery/magic link never ends up authenticated on the web.
 *
 * The exact message shown depends on the link type, which Supabase appends
 * to the redirect URL as a query / hash parameter (`type=signup |
 * magiclink | recovery | invite | email_change`).
 */

type LinkType =
  | 'signup'
  | 'magiclink'
  | 'recovery'
  | 'invite'
  | 'email_change'
  | 'unknown';

function detectLinkType(): LinkType {
  // Supabase puts the params either in the query string or in the URL hash
  // depending on flow type, so we check both.
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const search = window.location.search.startsWith('?')
    ? window.location.search.slice(1)
    : window.location.search;
  const params = new URLSearchParams(hash || search);
  const type = (params.get('type') ?? '').toLowerCase();
  if (
    type === 'signup' ||
    type === 'magiclink' ||
    type === 'recovery' ||
    type === 'invite' ||
    type === 'email_change'
  ) {
    return type;
  }
  return 'unknown';
}

export function AuthConfirmedPage() {
  const linkType = useMemo<LinkType>(() => detectLinkType(), []);
  const [signedOut, setSignedOut] = useState(false);

  const isRecovery = linkType === 'magiclink' || linkType === 'recovery';

  // For magic-link / recovery flows we don't want the user to remain
  // authenticated on the web (the dashboard is for operators only and these
  // flows are meant to be completed inside the mobile app). Sign out
  // immediately on mount.
  useEffect(() => {
    if (isRecovery) {
      supabase.auth.signOut().finally(() => setSignedOut(true));
    } else {
      setSignedOut(true);
    }
  }, [isRecovery]);

  const title = isRecovery
    ? 'Restablece tu contraseña desde la app'
    : '¡Correo confirmado!';

  const subtitle = isRecovery ? (
    <>
      Por seguridad, el restablecimiento de contraseña se completa dentro de la
      aplicación móvil <strong>ASI Security</strong>. Vuelve a la app e ingresa
      el <strong>código de 6 dígitos</strong> que recibiste por correo.
    </>
  ) : (
    <>
      Tu cuenta de <strong>ASI Security</strong> ya está activa. Vuelve a la
      aplicación móvil e inicia sesión con tu correo y contraseña.
    </>
  );

  const hintIcon = isRecovery ? (
    <KeyRound size={22} color="#0A84FF" />
  ) : (
    <Smartphone size={22} color="#0A84FF" />
  );

  const hintText = isRecovery ? (
    <>
      Abre la app <strong>ASI Security</strong> en tu teléfono y pega el código
      de 6 dígitos en la pantalla <em>Restablecer contraseña</em>.
    </>
  ) : (
    <>
      Abre la app <strong>ASI Security</strong> en tu teléfono y toca
      <em> Ya confirmé, ingresar</em>.
    </>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        background:
          'linear-gradient(180deg, #010816 0%, #02112B 50%, #04193D 100%)',
      }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8 text-center"
        style={{
          background: 'rgba(10, 20, 45, 0.55)',
          border: '1px solid rgba(184, 197, 224, 0.18)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
        }}
      >
        <img
          src="/logo_dark.png"
          alt="ASI Security"
          className="mx-auto mb-6 h-16 w-auto"
        />

        <div
          className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
          style={{
            background: 'rgba(10, 132, 255, 0.15)',
            border: '2px solid #0A84FF',
          }}
        >
          {isRecovery ? (
            <KeyRound size={44} color="#0A84FF" strokeWidth={2.2} />
          ) : (
            <CheckCircle2 size={44} color="#0A84FF" strokeWidth={2.2} />
          )}
        </div>

        <h1 className="text-2xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>
          {title}
        </h1>
        <p className="text-base mb-8" style={{ color: '#B8C5E0' }}>
          {subtitle}
        </p>

        <div
          className="flex items-center gap-3 rounded-xl p-4 text-left"
          style={{
            background: 'rgba(10, 132, 255, 0.08)',
            border: '1px solid rgba(10, 132, 255, 0.25)',
          }}
        >
          {hintIcon}
          <p className="text-sm" style={{ color: '#B8C5E0' }}>
            {hintText}
          </p>
        </div>

        {!signedOut && isRecovery && (
          <p className="mt-4 text-xs" style={{ color: '#7A8AA8' }}>
            Cerrando sesión…
          </p>
        )}

        <p className="mt-8 text-xs" style={{ color: '#7A8AA8' }}>
          Corporación A.S.I. · Guatemala
        </p>
      </div>
    </div>
  );
}

export default AuthConfirmedPage;
